export type CountTone = 'blue' | 'active' | 'slate' | 'red' | 'amber';

/*
  THE EDGE IS A `border`, NEVER A `ring` — product call, 06 Oct 2026, in the reader's own
  words: *the folder nav badge borders are still distorted.*

  A ring is a box-shadow drawn OUTSIDE the element's box, so any clipping ancestor cuts it.
  On a folder tab the badge sits inside `TAB_LABEL`'s `truncate` span — `overflow: hidden`,
  content box 18.6px — and the badge needs 20px (18 + 1 ring each side). MEASURED on the
  live strip: the ring was cut 0.44px at the top, 1px at the bottom and 1px on the right,
  which drew the pill with three flat edges.

  A border is inside the box and `box-sizing: border-box` is global, so the badge stays
  exactly 18 x 18 and nothing can clip the edge. It also makes the 18px rule TRUE: with the
  ring the badge was really 20px across.

  Do not revert to `ring-*` to "match" another control — the clip comes back the moment a
  badge sits in any `truncate`, `overflow-hidden` or rounded container, which is most of
  the places this is used.
*/
const TONES: Record<CountTone, string> = {
  blue: 'bg-blue-50 text-blue-700 border border-blue-200',
  // No edge: for use inside a control that is already blue.
  active: 'bg-blue-100 text-blue-700',
  slate: 'bg-slate-100 text-slate-600 border border-slate-200',
  red: 'bg-red-50 text-red-700 border border-red-200',
  amber: 'bg-amber-50 text-amber-700 border border-amber-200',
};

/**
 * Small count pill — reference §14. Replaces inline `(N)` in a label string.
 *
 * The undefined/zero distinction is load-bearing:
 *   `undefined` → renders nothing  = "not loaded yet"
 *   `0`         → visible `0` pill = "loaded, and there are none"
 * Collapsing the two would make an in-flight request look like an empty result.
 */
export function CountBadge({
  count,
  tone = 'blue',
  className = '',
}: {
  count: number | undefined;
  tone?: CountTone;
  className?: string;
}) {
  if (count === undefined) {
    return null;
  }

  /*
    A FIXED 18px HIGH, AND NEVER NARROWER THAN IT IS TALL — product call, 05 Oct 2026, in the
    reader's own words: *the badge is distorted, apply height changes if needed to correct.*

    It was sized by its content: `py-0.5` over an 11px line gave 21px of height, and one digit
    is 18.6px wide — so a single-count badge measured 18.6 x 21 and `rounded-full` drew a
    VERTICAL EGG rather than a circle. Two digits reached 25.2 x 21, a stadium so short it read
    as an oval. The shape was wrong on every badge in the product, not on one surface.

    `min-w` equal to the height is the half that actually fixes it: the height alone leaves
    one digit narrower than it is tall. The height is what the reader offered to spend, and
    3px is all it cost.

    MEASURED NOW, with the border below: one digit is 20.6 x 18 (ratio 1.14) and "10" is
    27.2 x 18 (1.51). The border is inside the box, so it adds 2px to the INTRINSIC width
    while `h-[18px]` pins the height — which is why one digit is a slightly wide circle
    rather than the 18 x 18 it measured under the ring. `min-w-[18px]` is consequently not
    binding at this font; it is kept as the floor for a narrower one.

    `leading-none` is load-bearing with a fixed height: the 11px text otherwise carries a 17px
    line box inside an 18px one, which leaves nothing to centre within and clips on a browser
    that rounds the other way.
  */
  return (
    <span
      className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-mono text-[11px] leading-none tabular-nums ${TONES[tone]} ${className}`}
    >
      {count}
    </span>
  );
}
